/**
 * ImageFieldInput.tsx
 *
 * Form field component for image capture with storage integration.
 * Wires ImageDropzone to ImageStorageService for seamless image upload,
 * preview, and form value management.
 */

// 1. External dependencies
import React, { useState, useEffect, useCallback, useRef } from 'react';

// 2. Internal absolute imports - services
import { imageStorageService } from '@/services/ImageStorageService';
import { BackgroundSyncService } from '@/services/sync/BackgroundSyncService';

// 3. Internal absolute imports - components
import { ImageDropzone, ImageLightbox } from '@/components/shared/ImageHandling';

// 4. Internal absolute imports - utilities
import { ImageProcessingError } from '@/utils/imageProcessing';

// 5. Internal absolute imports - types
import type { ImageReference } from '@/types/schema/StoredImage';
import type { BinarySupportedProvider } from '@/utils/syncProviderUtils';

// =============================================================================
// Types
// =============================================================================

/**
 * Props for ImageFieldInput component
 */
export interface ImageFieldInputProps {
  /** Field identifier for form state */
  fieldId: string;
  /** Current value (imageId reference) */
  value: ImageReference | null;
  /** Form callback when value changes */
  onChange: (value: ImageReference | null) => void;
  /** Field label */
  label?: string | undefined;
  /** Field description */
  description?: string | undefined;
  /** Validation error from form */
  error?: string | undefined;
  /** Disable all interactions */
  disabled?: boolean | undefined;
}

// =============================================================================
// Component
// =============================================================================

/**
 * ImageFieldInput Component
 *
 * Integrates ImageDropzone with ImageStorageService for form-based image capture.
 * Handles upload, preview URL lifecycle, error states, and retry functionality.
 */
export const ImageFieldInput: React.FC<ImageFieldInputProps> = ({
  fieldId: _fieldId,
  value,
  onChange,
  label,
  description,
  error,
  disabled = false,
}) => {
  // ===========================================================================
  // State
  // ===========================================================================

  /** Object URL for thumbnail display */
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  /** Upload in progress */
  const [isLoading, setIsLoading] = useState(false);

  /** Error message from storage service */
  const [uploadError, setUploadError] = useState<string | null>(null);

  /** Cached file for retry */
  const [lastFile, setLastFile] = useState<File | null>(null);

  /** Lightbox modal visibility */
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  /** Fetching remote blob in progress */
  const [isFetchingRemote, setIsFetchingRemote] = useState(false);

  /** Retry counter - incremented on image error to trigger re-fetch */
  const [retryCount, setRetryCount] = useState(0);

  /** Track current imageId to handle cleanup on change */
  const currentImageIdRef = useRef<string | null>(null);

  /** Track if we've auto-retried for the current imageId (prevents infinite loops) */
  const hasAutoRetriedRef = useRef<string | null>(null);

  // ===========================================================================
  // Effects
  // ===========================================================================

  /**
   * Fetch preview URL when value.imageId changes
   * If local blob is missing, attempts lazy-fetch from remote storage.
   * Cleanup: revoke old URL when imageId changes or on unmount
   */
  useEffect(() => {
    const imageId = value?.imageId ?? null;
    let isCancelled = false;

    // Skip if imageId hasn't changed AND this isn't a retry
    // (retryCount in deps allows re-running for same imageId)
    if (imageId === currentImageIdRef.current && retryCount === 0) {
      return undefined;
    }

    // Reset isFetchingRemote after early-return check (prevents flicker on unrelated effect re-runs)
    setIsFetchingRemote(false);

    // Revoke old URL if exists (only on imageId change, not retry)
    if (imageId !== currentImageIdRef.current && previewUrl) {
      imageStorageService.revokeAllUrls(currentImageIdRef.current ?? undefined);
      setPreviewUrl(null);
    }

    // Reset auto-retry flag when imageId changes (allows fresh auto-retry for new image)
    if (imageId !== currentImageIdRef.current) {
      hasAutoRetriedRef.current = null;
    }

    // Update ref
    currentImageIdRef.current = imageId;

    // Clear any previous errors when imageId changes or retry triggered
    setUploadError(null);

    // Fetch new URL if imageId exists
    if (imageId) {
      const fetchImageUrl = async (): Promise<void> => {
        try {
          // First try to get from local storage
          // Wrapped in isolated try/catch: iOS IndexedDB can throw after navigation
          let localUrl: string | null = null;
          try {
            localUrl = await imageStorageService.getImageUrl(imageId);
          } catch (localErr) {
            // iOS Safari/Chrome IndexedDB can become stale after navigation
            // Clear any stale preview and fall back to remote fetch
            if (!isCancelled) {
              setPreviewUrl(null);
            }
            console.warn(
              '[ImageFieldInput] Local getImageUrl failed, falling back to remote fetch',
              localErr
            );
            // Don't rethrow - proceed to remote fetch with localUrl = null
          }

          if (isCancelled) return;

          if (localUrl) {
            setPreviewUrl(localUrl);
            setRetryCount(0); // Reset after successful load (GPT feedback: before return)
            hasAutoRetriedRef.current = null; // Reset auto-retry flag on success
            return;
          }

          // Local blob not found - attempt lazy-fetch from remote
          // Clear stale preview and show loading state
          setPreviewUrl(null);
          setIsFetchingRemote(true);

          // Get sync provider
          const provider = BackgroundSyncService.getInstance().getProvider();

          if (!provider) {
            if (!isCancelled) {
              setIsFetchingRemote(false);
              // No error shown - image may be available later when sync connects
              // Just leave the dropzone empty
            }
            return;
          }

          // Check if provider supports binary operations
          if (
            !provider.supportsBinary ||
            !provider.readBinary ||
            !provider.existsBinary
          ) {
            if (!isCancelled) {
              setIsFetchingRemote(false);
              setUploadError('Image sync not available with current provider.');
            }
            return;
          }

          // Check if provider is connected (GPT feedback)
          const isConnected = await provider.isConnected();
          if (isCancelled) return;

          if (!isConnected) {
            setIsFetchingRemote(false);
            setUploadError('Sync not connected. Image may be available after reconnecting.');
            return;
          }

          // Attempt to fetch blob from remote
          const fetchSuccess = await imageStorageService.fetchRemoteBlob(
            imageId,
            provider as BinarySupportedProvider
          );

          if (isCancelled) return;

          if (fetchSuccess) {
            // Blob fetched successfully - retry getting the URL
            // Wrapped in try/catch: iOS IndexedDB can also throw here after navigation
            let remoteUrl: string | null = null;
            try {
              remoteUrl = await imageStorageService.getImageUrl(imageId);
            } catch (remoteErr) {
              console.warn(
                '[ImageFieldInput] getImageUrl failed after remote fetch, will retry',
                remoteErr
              );
              // Don't rethrow - set error so retry button shows
              if (!isCancelled) {
                setUploadError('Failed to load image. Tap Retry.');
              }
              return;
            }

            if (!isCancelled && remoteUrl) {
              setPreviewUrl(remoteUrl);
              setRetryCount(0); // Reset after successful load (GPT feedback: after setPreviewUrl)
              hasAutoRetriedRef.current = null; // Reset auto-retry flag on success
            }
          } else {
            // Fetch failed - image may not exist in remote storage
            setUploadError('Image not available.');
          }
        } catch {
          if (!isCancelled) {
            setPreviewUrl(null);
            setUploadError('Failed to load image. Tap Retry.');
          }
        } finally {
          if (!isCancelled) {
            setIsFetchingRemote(false);
          }
        }
      };

      void fetchImageUrl();
    }

    return () => {
      isCancelled = true;
    };
  }, [value?.imageId, previewUrl, retryCount]);

  /**
   * Cleanup on unmount: revoke any active preview URL
   */
  useEffect(() => {
    return () => {
      if (currentImageIdRef.current) {
        imageStorageService.revokeAllUrls(currentImageIdRef.current);
      }
    };
  }, []);

  // ===========================================================================
  // Handlers
  // ===========================================================================

  /**
   * Handle file selection from ImageDropzone
   * Uploads to ImageStorageService and updates form value
   */
  const handleFileSelected = useCallback(
    async (file: File) => {
      setIsLoading(true);
      setUploadError(null);
      setLastFile(file);

      try {
        const result = await imageStorageService.storeImage(file);

        // Success: update form value and clear retry state
        onChange({ type: 'image', imageId: result.image.id });
        setLastFile(null);
        setUploadError(null);
      } catch (err) {
        // Handle specific error types
        if (err instanceof ImageProcessingError) {
          setUploadError(err.message);
        } else {
          setUploadError('Upload failed. Please try again.');
        }
      } finally {
        setIsLoading(false);
      }
    },
    [onChange]
  );

  /**
   * Handle clear button click
   * Clears form value and revokes preview URL
   */
  const handleClear = useCallback(() => {
    // Revoke URL before clearing
    if (currentImageIdRef.current) {
      imageStorageService.revokeAllUrls(currentImageIdRef.current);
    }

    // Clear state to prevent stale UI
    setPreviewUrl(null);
    currentImageIdRef.current = null;

    // Clear form value
    onChange(null);

    // Clear any error/retry state
    setUploadError(null);
    setLastFile(null);

    // Reset auto-retry flag (imageId becomes null)
    hasAutoRetriedRef.current = null;
  }, [onChange]);

  /**
   * Handle retry button click for upload errors
   * Re-attempts upload with cached file
   */
  const handleUploadRetry = useCallback(() => {
    if (lastFile) {
      handleFileSelected(lastFile);
    }
  }, [lastFile, handleFileSelected]);

  /**
   * Handle retry button click for load errors
   * Increments retryCount to trigger effect re-run
   */
  const handleLoadRetry = useCallback(() => {
    setUploadError(null);
    setRetryCount((prev) => prev + 1);
  }, []);

  /**
   * Handle preview click for lightbox
   * Only opens if previewUrl exists
   */
  const handlePreviewClick = useCallback(() => {
    if (previewUrl) {
      setIsLightboxOpen(true);
    }
  }, [previewUrl]);

  /**
   * Handle dropzone validation errors
   */
  const handleDropzoneError = useCallback((message: string) => {
    setUploadError(message);
  }, []);

  /**
   * Handle image element load error (stale URL)
   * Invalidates corrupt local blob and attempts one auto-retry before showing manual retry UI.
   */
  const handleImageError = useCallback(() => {
    // Guard: ignore if we're already fetching or uploading (prevents stacking retries)
    if (isFetchingRemote || isLoading) return;

    const currentImageId = currentImageIdRef.current;

    // Clear stale preview URL
    setPreviewUrl(null);

    // Check if we should auto-retry (only once per imageId)
    if (hasAutoRetriedRef.current !== currentImageId) {
      // First error for this imageId - attempt auto-retry
      hasAutoRetriedRef.current = currentImageId;

      // Delete corrupt local blob, then trigger retry after deletion completes
      // This prevents race condition where effect runs before blob is deleted
      if (currentImageId) {
        void imageStorageService
          .invalidateLocalBlob(currentImageId)
          .then(() => {
            setRetryCount((prev) => prev + 1); // Triggers effect re-run
          })
          .catch(() => {
            // Even if invalidation fails, still trigger retry
            setRetryCount((prev) => prev + 1);
          });
      } else {
        setRetryCount((prev) => prev + 1);
      }
      // Don't set error state - let the retry attempt run
    } else {
      // Already auto-retried for this imageId - show manual retry UI
      setUploadError('Failed to load image. Tap Retry.');
      setRetryCount(0); // Reset retryCount so skip guard works correctly
    }
  }, [isFetchingRemote, isLoading]);

  // ===========================================================================
  // Render
  // ===========================================================================

  // Combine upload error with form validation error (upload error takes precedence during upload)
  const displayError = uploadError || error || null;

  return (
    <>
      <ImageDropzone
        previewUrl={previewUrl}
        onFileSelected={handleFileSelected}
        onClear={handleClear}
        onPreviewClick={previewUrl ? handlePreviewClick : undefined}
        onError={handleDropzoneError}
        onImageError={handleImageError}
        error={displayError}
        onRetry={lastFile ? handleUploadRetry : (uploadError && value?.imageId ? handleLoadRetry : undefined)}
        isLoading={isLoading}
        isFetchingRemote={isFetchingRemote}
        label={label}
        description={description}
        disabled={disabled}
        hasExistingImage={!!value?.imageId}
      />

      <ImageLightbox
        isOpen={isLightboxOpen}
        onClose={() => setIsLightboxOpen(false)}
        imageUrl={previewUrl}
        alt={label ? `${label} - full size` : 'Image preview'}
      />
    </>
  );
};

export default ImageFieldInput;
